import { merge, pick, set } from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';

import { useActions, useCollections } from '@mssgme/hooks';
import { handleError } from '@mssgme/lib';
import { Notification } from '@mssgme/ui';

export const useProductCollectionEdit = () => {
    const { t } = useTranslation('cards');
    const actions = useActions();
    const history = useHistory();
    const match = useRouteMatch();
    const collectionId = match.params.id || null;
    const collections = useCollections();
    const currentCollection = collections.items.find((col) => col.id === collectionId) || {
        name: '',
        slug: '',
        default: false,
        published: true,
    };
    const isFetching = collections.isFetching;
    const [hasChanges, setHasChanges] = useState(false);
    const [collection, setCollection] = useState(currentCollection);
    const [errors, setErrors] = useState({});
    const isNew = !collectionId;

    const handleChange = ({ target: { type, name, value: raw, checked } }) => {
        const value = type === 'checkbox' ? checked : raw;

        setCollection((prevCollection) => set(merge({}, prevCollection), name, value));
        setHasChanges(true);
    };

    const handleErrors = (e) => {
        if (e.errors) {
            setErrors(e.errors);
        } else {
            handleError(e);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        setCollection(collection);

        const isNew = !collectionId;
        const payload = pick(collection, ['name', 'slug', 'published', 'default']);

        try {
            if (isNew) {
                await actions.api.collections.create({ payload });
            } else {
                await actions.api.collections.update({ collectionId, payload });
            }

            setHasChanges(false);

            Notification.notify(t('CardEditor.saved'), { showClose: true });
            history.push(`/p/products/collections/${collectionId || ''}`);
        } catch (e) {
            handleErrors(e);
        }
    };

    const handleRemoveClick = async (e) => {
        e.preventDefault();

        try {
            await actions.api.collections.delete({ collectionId });

            setHasChanges(false);

            Notification.notify(t('CardEditor.removed'), { showClose: true });
            history.push('/p/products/collections');
        } catch (e) {
            handleErrors(e);
        }
    };

    useEffect(() => {
        if (collectionId && !collection.id) {
            setCollection(currentCollection);
        }
    }, [currentCollection]);

    return {
        t,
        collectionId,
        collection,
        hasChanges,
        isFetching,
        isNew,
        errors,
        handleSubmit,
        handleChange,
        handleRemoveClick,
    };
};
